import React, { forwardRef, useState, useCallback } from 'react';
import cx from 'classnames';
import * as styles from './Button.module.css';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  color?:
    | 'default'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'accent'
    | 'white'
    | 'error';
  size?: 'tiny' | 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  topIcon?: React.ReactNode;
  className?: string;
}

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  color?: 'default' | 'inherit' | 'primary' | 'secondary' | 'accent';
  size?: 'tiny' | 'small' | 'medium' | 'large';
  className?: string;
}

interface Ripple {
  x: number;
  y: number;
  size: number;
  id: number;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'contained',
      color = 'primary',
      size = 'medium',
      fullWidth = false,
      disabled = false,
      startIcon = null,
      endIcon = null,
      topIcon = null,
      onClick = undefined,
      className = undefined,
      type = 'button',
      ...rest
    },
    ref,
  ) => {
    const [ripples, setRipples] = useState<Ripple[]>([]);

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;

        const button = e.currentTarget;
        const rect = button.getBoundingClientRect();
        const rippleSize = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - rippleSize / 2;
        const y = e.clientY - rect.top - rippleSize / 2;
        const id = Date.now();

        setRipples(prev => [...prev, { x, y, size: rippleSize, id }]);
        setTimeout(() => {
          setRipples(prev => prev.filter(r => r.id !== id));
        }, 600);

        if (onClick) onClick(e);
      },
      [disabled, onClick],
    );

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        onClick={handleClick}
        className={cx(
          styles.button,
          styles[variant],
          styles[color === 'default' ? 'colorDefault' : color],
          styles[size],
          { [styles.fullWidth]: fullWidth },
          className,
        )}
        {...rest}
      >
        <span
          className={cx(styles.buttonContent, {
            [styles.endIcon]: !!endIcon,
            [styles.startIcon]: !!startIcon,
            [styles.topIcon]: !!topIcon,
          })}
        >
          {startIcon && <span>{startIcon}</span>}
          {topIcon && <span>{topIcon}</span>}
          {children}
          {endIcon && <span>{endIcon}</span>}
        </span>
        {!disabled &&
          variant !== 'text' &&
          ripples.map(r => (
            <span
              key={r.id}
              className={styles.ripple}
              style={{
                width: r.size,
                height: r.size,
                left: r.x,
                top: r.y,
              }}
            />
          ))}
      </button>
    );
  },
);

Button.displayName = 'Button';

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      children,
      color = 'default',
      size = 'medium',
      disabled = false,
      onClick = undefined,
      className = undefined,
      ...rest
    },
    ref,
  ) => (
    <Button
      ref={ref}
      variant="text"
      color={color}
      size={size}
      disabled={disabled}
      onClick={onClick}
      className={cx(styles.rounded, className)}
      {...rest}
    >
      {children}
    </Button>
  ),
);

IconButton.displayName = 'IconButton';

export { Button, IconButton };
export default Button;
